import { Pencil, Search, SquarePlus, Trash2 } from "lucide-react";
import { useRef, useState } from "react";
import AddPromptDialog from "../../components/prompt/AddPromptDialog";
import EditPromptDialog from "../../components/prompt/EditPromptDialog";
import ConfirmDialog from "../../components/common/ConfirmDialog";
import {
  useDeletePrompt,
  useGetPromptList,
  usePostSetCurrent,
} from "../../query/usePrompt";
import { generateRandomString, timeAgo } from "../../lib/utils";
import { toast } from "sonner";
import { AxiosError } from "axios";
import { GetPromptListReturn } from "../../types/types";
import ButtonLoading from "../../components/common/ButtonLoading";

const Prompt = () => {
  const [isOpenAddPromptDialog, setIsOpenAddPromptDialog] =
    useState<boolean>(false);
  const [isOpenEditPromptDialog, setIsOpenEditPromptDialog] =
    useState<boolean>(false);
  const [isOpenDeletePromptDialog, setIsOpenDeletePromptDialog] =
    useState<boolean>(false);
  const [deletePromptId, setDeletePromptId] = useState<number>(0);
  const [title, setTitle] = useState<string>("");
  const [toUpdatePrompt, setToUpdatePrompt] = useState<
    GetPromptListReturn | undefined
  >();

  const { data, refetch } = useGetPromptList();

  const { mutate, isPending } = useDeletePrompt();
  const { mutate: setCurrent, isPending: isSetCurrentLoading } =
    usePostSetCurrent();

  const handlePromptDelete = () => {
    mutate(deletePromptId, {
      onSuccess: (res) => {
        if (res?.message) {
          toast.success(res?.message);
        } else {
          toast.success("Prompt has been deleted");
        }
        setIsOpenDeletePromptDialog(false);
        refetch();
      },
      onError: (err) => {
        if (err instanceof AxiosError) {
          if (err.response.data.message) {
            toast.error(err.response.data.message);
          } else {
            toast.error("Something went wrong");
          }
        }
      },
    });
  };

  const handleSetCurrent = (id: number) => {
    setCurrent(id, {
      onSuccess: (res) => {
        if (res?.message) {
          toast.success(res?.message);
        } else {
          toast.success("Prompt has been set as current prompt");
        }
        setIsOpenDeletePromptDialog(false);
        refetch();
      },
      onError: (err) => {
        if (err instanceof AxiosError) {
          if (err.response.data.message) {
            toast.error(err.response.data.message);
          } else {
            toast.error("Something went wrong");
          }
        }
      },
    });
  };

  return (
    <div className="font-poppins">
      <button
        className="w-[233px] h-[61px] flex justify-center items-center gap-x-3 bg-lightpurple rounded-lg font-medium text-[#615e83] border-lightpurple hover:border-[#615e83]"
        onClick={() => setIsOpenAddPromptDialog(true)}
      >
        <p>New Prompt</p>
        <SquarePlus />
      </button>
      <div className="w-full bg-lightpurple rounded-2xl min-h-[calc(100vh-220px)] max-h-[calc(100vh-220px)] p-5 space-y-5 mt-5">
        <p className="font-semibold text-[22px]">List of Prompts</p>
        <div className="flex items-center gap-x-5">
          <div className="w-full lg:w-[450px] h-10 relative">
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              className="w-full bg-white rounded-lg h-full pl-10"
            />
            <Search className="w-5 h-5 absolute top-1/2 -translate-y-1/2 left-3 text-[#615E83]" />
          </div>
          {/* <button className="text-white bg-primary h-10 rounded-lg px-5 border border-primary hover:bg-white hover:text-primary">
            Search
          </button> */}
        </div>
        <div className="w-full  min-h-[calc(100vh-370px)] max-h-[calc(100vh-370px)] overflow-y-auto scrollbar-thin">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
            {data
              ?.filter(
                (item) =>
                  item.title.toLowerCase().indexOf(title.toLowerCase()) > -1
              )
              ?.map((item, i) => (
                <div key={i} className="w-full bg-white rounded-lg p-5 h-fit">
                  <div className="flex items-center justify-between">
                    <p className="font-medium">{item?.title}</p>
                    <div className="flex items-center gap-x-3">
                      {item?.is_active ? (
                        <p className="text-green-500 text-sm">Current Prompt</p>
                      ) : (
                        <button
                          className="px-3 py-1.5 rounded-lg bg-primary text-white border border-primary hover:bg-white hover:text-primary transition-all duration-200 text-sm"
                          disabled={isSetCurrentLoading}
                          onClick={() => handleSetCurrent(item?.id)}
                        >
                          {isSetCurrentLoading ? (
                            <ButtonLoading />
                          ) : (
                            "Set current"
                          )}
                        </button>
                      )}
                      <Pencil
                        className="text-[#888] w-5 h-5 cursor-pointer"
                        data-testid="icon-pencil"
                        onClick={() => {
                          setIsOpenEditPromptDialog(true);
                          setToUpdatePrompt(item);
                        }}
                      />
                    </div>
                  </div>
                  <p className="text-sm text-[#4a4a4a] mt-3 line-clamp-3">
                    {item?.context}
                  </p>
                  <div className="flex items-center justify-between text-[#888] mt-5">
                    <div className="flex items-center gap-5 flex-wrap  text-xs">
                      <p>{item?.created_by}</p>
                      <p>
                        Edited{" "}
                        {item?.last_modified && timeAgo(item?.last_modified)}
                      </p>
                    </div>
                    <Trash2
                      className="w-5 h-5 cursor-pointer"
                      data-testid="icon-trash"
                      onClick={() => {
                        setIsOpenDeletePromptDialog(true);
                        setDeletePromptId(item?.id);
                      }}
                    />
                  </div>
                </div>
              ))}
          </div>
        </div>
      </div>
      <AddPromptDialog
        isOpenDialog={isOpenAddPromptDialog}
        setIsOpenDialog={setIsOpenAddPromptDialog}
        refetch={refetch}
        key={generateRandomString(2)}
      />
      <EditPromptDialog
        isOpenDialog={isOpenEditPromptDialog}
        setIsOpenDialog={setIsOpenEditPromptDialog}
        refetch={refetch}
        data={toUpdatePrompt}
        key={toUpdatePrompt?.id}
      />
      <ConfirmDialog
        isOpenDialog={isOpenDeletePromptDialog}
        setIsOpenDialog={setIsOpenDeletePromptDialog}
        title="Are you sure you want to delete the prompt?"
        confirmBtnText="Delete"
        confirmAction={handlePromptDelete}
        isPending={isPending}
      />
    </div>
  );
};

export default Prompt;
